// external imports
import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import { Close, TranslateOutlined } from '@mui/icons-material';

/**
 * Add / Edit dialog for a language config. Both flows submit to the same upsert
 * endpoint (POST /task/langconfig); Edit simply locks the language and pre-fills
 * the current translator.
 *
 * onSubmit({ languageCode, translatorEmpId })
 */
const LanguageConfigModal = ({
  open,
  editingConfig, // row object when editing, null when adding
  languages = [], // supported languages for the dropdown (add flow)
  usedLanguageCodes = [], // codes already configured — disabled while adding
  submitting = false,
  formError = null, // inline API error ("message" field)
  onClose,
  onSubmit,
}) => {
  const isEdit = !!editingConfig;

  const [languageCode, setLanguageCode] = React.useState('');
  const [translatorEmpId, setTranslatorEmpId] = React.useState('');
  const [localError, setLocalError] = React.useState('');

  // seed / reset the form whenever the dialog opens or the target row changes
  React.useEffect(() => {
    if (!open) return;
    setLocalError('');
    if (editingConfig) {
      setLanguageCode(editingConfig.languageCode || '');
      setTranslatorEmpId(
        editingConfig.translators?.[0]?.employeeId != null
          ? String(editingConfig.translators[0].employeeId)
          : ''
      );
    } else {
      setLanguageCode('');
      setTranslatorEmpId('');
    }
  }, [open, editingConfig]);

  const handleSubmit = () => {
    if (!languageCode) {
      setLocalError('Please select a language.');
      return;
    }
    const trimmed = String(translatorEmpId).trim();
    if (!trimmed) {
      setLocalError('Please enter a translator employee ID.');
      return;
    }
    if (!/^\d+$/.test(trimmed)) {
      setLocalError('Employee ID must be a number.');
      return;
    }
    setLocalError('');
    onSubmit({ languageCode, translatorEmpId: trimmed });
  };

  // In edit mode we only ever show the one language; in add mode, list all
  // supported languages and disable those already configured.
  const languageOptions = React.useMemo(() => {
    if (isEdit) {
      return [{ code: editingConfig.languageCode, name: editingConfig.languageName }];
    }
    return languages;
  }, [isEdit, editingConfig, languages]);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: '16px', width: '100%', maxWidth: 460 } }}
    >
      {/* header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          p: 3,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TranslateOutlined sx={{ color: '#6366f1' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 19, fontWeight: 700, color: '#0f172a' }}>
              {isEdit ? 'Edit Language Config' : 'Add Language Config'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              {isEdit
                ? 'Update the translator assigned to this language.'
                : 'Map a target language to the translator responsible for it.'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <Close sx={{ fontSize: 20, color: '#94a3b8' }} />
        </IconButton>
      </Box>

      {/* body */}
      <Box sx={{ px: 3, pb: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Language dropdown */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155', mb: 0.75 }}>
            Language
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={languageCode}
            onChange={(e) => setLanguageCode(e.target.value)}
            disabled={isEdit}
            placeholder="Select a language"
            SelectProps={{ displayEmpty: true }}
            InputProps={{ sx: { borderRadius: '10px', background: isEdit ? '#f1f5f9' : '#fff' } }}
          >
            <MenuItem value="" disabled>
              <span style={{ color: '#94a3b8' }}>Select a language</span>
            </MenuItem>
            {languageOptions.map((lang) => {
              const alreadyUsed = !isEdit && usedLanguageCodes.includes(lang.code);
              return (
                <MenuItem key={lang.code} value={lang.code} disabled={alreadyUsed}>
                  {lang.name}
                  <span style={{ color: '#94a3b8', marginLeft: 6, fontFamily: 'monospace' }}>
                    ({lang.code})
                  </span>
                  {alreadyUsed && (
                    <span style={{ color: '#cbd5e1', marginLeft: 8, fontSize: 12 }}>
                      · already configured
                    </span>
                  )}
                </MenuItem>
              );
            })}
          </TextField>
        </Box>

        {/* Translator employee ID */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155', mb: 0.75 }}>
            Translator Employee ID
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            value={translatorEmpId}
            onChange={(e) => setTranslatorEmpId(e.target.value)}
            placeholder="e.g. 12345"
            InputProps={{ sx: { borderRadius: '10px' } }}
          />
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.75 }}>
            The employee ID of the translator responsible for this language.
          </Typography>
        </Box>

        {/* validation / API error */}
        {(localError || formError) && (
          <Alert severity="error" sx={{ borderRadius: '10px', py: 0.25 }}>
            {localError || formError}
          </Alert>
        )}
      </Box>

      {/* footer */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={submitting}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            borderColor: '#e2e8f0',
            color: '#475569',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            px: 3,
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' },
          }}
        >
          {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Config'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default LanguageConfigModal;
